'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { useVoterSearch } from '@/hooks/use-voter-search';
import { offlineDB, type OfflineVoter } from '@/lib/offline-db';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Search,
  X,
  User,
  PenTool,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Fingerprint,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export function VoterSearchScreen() {
  const { setCurrentView, setSelectedVoter } = useAppStore();
  const { user } = useAuthStore();
  const { results, isSearching, search, clearResults } = useVoterSearch();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVoterLocal, setSelectedVoterLocal] = useState<OfflineVoter | null>(null);

  const boothId = user?.boothId || '';

  // Derive the display voter: explicitly selected, or single result auto-select
  const displayVoter = useMemo(() => {
    if (selectedVoterLocal) return selectedVoterLocal;
    if (results.length === 1) return results[0];
    return null;
  }, [selectedVoterLocal, results]);

  const handleSearch = useCallback(async () => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      toast({
        title: 'खोजें',
        description: 'कृपया मतदाता अनुक्रमांक या EPiC नंबर दर्ज करें',
        variant: 'destructive',
      });
      return;
    }
    setSelectedVoterLocal(null);
    await search(trimmed);
  }, [searchQuery, search, toast]);

  const handleReset = useCallback(() => {
    setSearchQuery('');
    setSelectedVoterLocal(null);
    clearResults();
  }, [clearResults]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleSelectVoter = useCallback((voter: OfflineVoter) => {
    setSelectedVoterLocal(voter);
  }, []);

  const handleVerify = useCallback(async () => {
    if (!displayVoter) return;

    // Check if this voter is already verified
    try {
      const verifications = await offlineDB.getVerifications(boothId);
      const alreadyVerified = verifications.some(
        (v) => v.epicNo === displayVoter.epicNo
      );

      if (alreadyVerified) {
        toast({
          title: 'पहले से सत्यापित',
          description: `${displayVoter.name} (EPiC: ${displayVoter.epicNo}) पहले से सत्यापित हैं`,
          variant: 'destructive',
        });
        return;
      }
    } catch {
      // Continue even if check fails
    }

    // Set selected voter in app store and navigate to verify screen
    setSelectedVoter(displayVoter as unknown as Record<string, unknown>);
    setCurrentView('voter-verify');
  }, [displayVoter, setSelectedVoter, setCurrentView, boothId, toast]);

  // Get initials for photo placeholder
  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Gender display helper
  const getGenderDisplay = (gender: string) => {
    if (!gender) return '-';
    const g = gender.toLowerCase();
    if (g === 'male' || g === 'पुरुष') return 'पुरुष';
    if (g === 'female' || g === 'महिला') return 'महिला';
    if (g === 'other' || g === 'अन्य') return 'अन्य';
    return gender;
  };

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 space-y-4">
      {/* Header */}
      <Card className="border-emerald-200 shadow-sm bg-gradient-to-r from-emerald-700 to-emerald-600">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">मतदाता खोज</h2>
              <p className="text-xs text-emerald-200">Voter Search</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Section */}
      <Card className="border-emerald-200 shadow-sm">
        <CardContent className="pt-5 pb-5">
          <div className="space-y-3">
            <label className="text-sm font-semibold text-emerald-800 block">
              मतदाता अनुक्रमांक / EPiC नंबर
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="अनुक्रमांक या EPiC..."
                  className="pl-9 sm:pl-10 h-11 sm:h-12 text-sm sm:text-base border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
                  disabled={isSearching}
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={isSearching}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 sm:h-12 px-3 sm:px-6 min-w-[60px] sm:min-w-[90px]"
              >
                {isSearching ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">खोज</span>
                  </>
                )}
              </Button>
              <Button
                onClick={handleReset}
                variant="outline"
                className="border-gray-300 text-gray-600 hover:bg-gray-50 h-11 sm:h-12 px-3 sm:px-4 min-w-[50px] sm:min-w-[80px]"
              >
                <X className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">रद्द</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Results - Multiple Voters List */}
      {results.length > 1 && !displayVoter && (
        <Card className="border-emerald-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-emerald-800">
              खोज परिणाम ({results.length} मिले)
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="max-h-72 overflow-y-auto space-y-2">
              {results.map((voter) => (
                <button
                  key={voter.id}
                  onClick={() => handleSelectVoter(voter)}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {getInitials(voter.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 truncate">
                        {voter.nameHindi || voter.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        EPiC: {voter.epicNo} | क्र. {voter.serialNo}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Results Message */}
      {results.length === 0 && searchQuery && !isSearching && (
        <Card className="border-amber-200 bg-amber-50 shadow-sm">
          <CardContent className="pt-5 pb-5">
            <div className="text-center">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <p className="font-semibold text-amber-800">कोई मतदाता नहीं मिला</p>
              <p className="text-sm text-amber-600 mt-1">
                कृपया सही अनुक्रमांक या EPiC नंबर दर्ज करें
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Voter Details */}
      {displayVoter && (
        <>
          {/* Voter Information Card */}
          <Card className="border-emerald-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-emerald-600" />
                <CardTitle className="text-base text-emerald-800">
                  मतदाता की जानकारी
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Left Section - Voter Details */}
                <div className="md:col-span-2 space-y-3">
                  <VoterDetailRow
                    label="मतदाता अनुक्रमांक"
                    value={String(displayVoter.serialNo)}
                  />
                  <VoterDetailRow
                    label="EPiC नंबर"
                    value={displayVoter.epicNo}
                  />
                  <VoterDetailRow
                    label="नाम"
                    value={displayVoter.nameHindi || displayVoter.name}
                    highlight
                  />
                  <VoterDetailRow
                    label="पिता/पति का नाम"
                    value={displayVoter.fatherHusbandName}
                  />
                  <VoterDetailRow
                    label="लिंग"
                    value={getGenderDisplay(displayVoter.gender)}
                  />
                  <VoterDetailRow
                    label="आयु"
                    value={`${displayVoter.age} वर्ष`}
                  />
                  <VoterDetailRow
                    label="पता"
                    value={
                      displayVoter.address ||
                      (displayVoter.houseNo
                        ? `मकान नं. ${displayVoter.houseNo}`
                        : '-')
                    }
                  />
                </div>

                {/* Right Section - Photo and Signature */}
                <div className="space-y-3">
                  {/* Photo Area */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 text-center py-1 px-2">
                      <span className="text-xs font-semibold text-gray-600">
                        फोटो
                      </span>
                    </div>
                    <div className="bg-white flex items-center justify-center p-3">
                      {displayVoter.photoUrl ? (
                        <img
                          src={displayVoter.photoUrl}
                          alt={displayVoter.name}
                          className="w-28 h-36 object-cover rounded"
                        />
                      ) : (
                        <div className="w-28 h-36 bg-emerald-50 rounded flex items-center justify-center border-2 border-dashed border-emerald-200">
                          <span className="text-3xl font-bold text-emerald-300">
                            {getInitials(
                              displayVoter.nameHindi ||
                                displayVoter.name
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 p-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs border-emerald-200 text-emerald-700 h-8"
                        onClick={handleVerify}
                      >
                        <Fingerprint className="w-3 h-3 mr-1" />
                        अंगूठे का निशान लें
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs border-emerald-200 text-emerald-700 h-8"
                        onClick={handleVerify}
                      >
                        <PenTool className="w-3 h-3 mr-1" />
                        हस्ताक्षर लें
                      </Button>
                    </div>
                  </div>

                  {/* Thumb Impression / Signature Area */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 text-center py-1 px-2">
                      <span className="text-xs font-semibold text-gray-600">
                        अंगुष्ठ छाप / हस्ताक्षर
                      </span>
                    </div>
                    <div className="bg-white flex items-center justify-center p-3">
                      <div className="w-28 h-20 bg-white border-2 border-gray-300 rounded flex items-center justify-center">
                        <Fingerprint className="w-8 h-8 text-gray-300" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Verify Button */}
          <Button
            onClick={handleVerify}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-base sm:text-lg h-12 sm:h-14 shadow-md"
          >
            <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
            हाँ माना
          </Button>
        </>
      )}
    </div>
  );
}

// Helper component for voter detail rows
function VoterDetailRow({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-b-0">
      <span className="text-xs sm:text-sm text-gray-500 min-w-[110px] sm:min-w-[140px] flex-shrink-0">
        {label}:
      </span>
      <span
        className={`text-xs sm:text-sm font-medium ${
          highlight ? 'text-emerald-800 text-sm sm:text-base' : 'text-gray-800'
        }`}
      >
        {value || '-'}
      </span>
    </div>
  );
}
